import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "../../lib/utils"

const Tabs = TabsPrimitive.Root

type TabsSize = "default" | "small"

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  size?: TabsSize
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, size = "default", ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center rounded-full bg-white font-ploni shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)]",
      size === "small" ? "gap-1 p-1" : "gap-0.5 p-1",
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  size?: TabsSize
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, size = "default", ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex max-w-[120px] items-center justify-center rounded-full border-none bg-transparent p-0 text-center font-ploni text-lg font-normal leading-none tracking-normal text-secondary-foreground hover:border-transparent hover:bg-transparent hover:outline-none focus-visible:outline-none focus-within:outline-none disabled:pointer-events-none data-[state=active]:bg-[linear-gradient(95.88deg,#BFDEDA_6.67%,#EAF4D8_90.78%)] data-[state=active]:shadow-none",
      size === "small" ? "h-[26px] px-3" : "h-10 px-3",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 font-ploni ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

interface TabContentProps {
  icon?: React.ReactNode
  iconPosition?: "start" | "end"
  children: React.ReactNode
  className?: string
}

const TabContent = React.forwardRef<HTMLDivElement, TabContentProps>(
  ({ icon, iconPosition = "start", children, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-1 justify-center w-full",
          className
        )}
      >
        {icon && iconPosition === "start" && (
          <div className="flex-shrink-0">{icon}</div>
        )}
        <div
          className={cn(
            "line-clamp-2 text-center w-full",
            icon ? "max-w-[88px]" : "max-w-[76px]"
          )}
          data-testid="tab-title"
        >
          {children}
        </div>
        {icon && iconPosition === "end" && (
          <div className="flex-shrink-0">{icon}</div>
        )}
      </div>
    )
  }
)
TabContent.displayName = "TabContent"

export { Tabs, TabsList, TabsTrigger, TabsContent, TabContent }
